import type { LatLon, PrecipField, SpotListItem, WindField } from "@divespots/contracts";
import "leaflet/dist/leaflet.css";
import type { Map as LeafletMap } from "leaflet";
import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { minZoomFor } from "./cover-zoom.ts";
import { SpotCluster } from "./SpotCluster.tsx";
import { L } from "./leaflet-global.ts";
import { PrecipLayer } from "./precip/PrecipLayer.tsx";
import { WindLayer } from "./wind/WindLayer.tsx";
import {
  BASE_LAYERS,
  BATHY_MIN_ZOOM,
  BATHY_OVERLAY,
  COVERAGE_BOUNDS,
  MAX_ZOOM,
  SWITZERLAND_BOUNDS,
  wmtsUrl,
} from "./tiles.ts";
import type { BaseLayer } from "./tiles.ts";

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

interface Props {
  spots: readonly SpotListItem[];
  layer: BaseLayer;
  selectedSlug: string | undefined;
  /** Location of the selected spot, once known (list item or detail). */
  selectedLocation: LatLon | undefined;
  /** Pixel shift so the spot lands in the visible part of the map, not under the sheet. */
  flyOffset: { x: number; y: number };
  onSelect: (slug: string) => void;
  onMapReady: (map: LeafletMap) => void;
  pinLabel: (spot: SpotListItem, closed: boolean) => string;
  clusterLabel: (count: number) => string;
  ariaLabel: string;
  /** The wind overlay, while it is on and its field has arrived. */
  wind?: { field: WindField; t: number } | undefined;
  /** The precipitation overlay, while it is on and its field has arrived. */
  precip?: { field: PrecipField; t: number } | undefined;
}

function MapBridge({ onReady, label }: { onReady: (map: LeafletMap) => void; label: string }) {
  const map = useMap();
  useEffect(() => onReady(map), [map, onReady]);
  // E2E builds only: the tests read the view (zoom, bounds) from here
  useEffect(() => {
    if (import.meta.env.VITE_E2E === "1") (window as unknown as { __map?: LeafletMap }).__map = map;
  }, [map]);
  useEffect(() => {
    const el = map.getContainer();
    el.setAttribute("role", "region");
    el.setAttribute("aria-label", label);
  }, [map, label]);
  return null;
}

/**
 * Keeps the viewport inside the swisstopo coverage. Min zoom = the lowest whole zoom at which the
 * whole viewport lies inside the layers' extent ("cover", see cover-zoom.ts), recomputed whenever
 * the map is resized (rotation, window); a desktop window may instead show the whole country.
 * maxBounds (viscosity 1) stops panning out. The first view is centred on Switzerland at that zoom,
 * clamped to the coverage. It opens one zoom level deeper than the minimum, so users can still zoom out once. Zooms stay whole because markercluster only works on whole zoom levels.
 */
function CoverageGuard() {
  const map = useMap();
  useEffect(() => {
    const minZoom = () => {
      const size = map.getSize();
      const bar = document.querySelector(".topbar")?.getBoundingClientRect();
      const top = (bar ? bar.bottom : size.x >= 900 ? 60 : 120) + 16;
      // floating UI the country must clear when it is shown whole: top bar, the control column, the credit
      const pad = { w: 16 + 44 + 16 + 16, h: top + 24 + 8 + 16 };
      return minZoomFor({ w: size.x, h: size.y }, COVERAGE_BOUNDS, SWITZERLAND_BOUNDS, pad, {
        min: 5,
        max: MAX_ZOOM - 1,
      });
    };
    const onResize = () => {
      map.options.minZoom = minZoom();
      if (map.getZoom() < map.options.minZoom) map.setZoom(map.options.minZoom, { animate: false });
      map.panInsideBounds(L.latLngBounds(COVERAGE_BOUNDS), { animate: false });
    };
    map.options.minZoom = minZoom();
    map.setView(L.latLngBounds(SWITZERLAND_BOUNDS).getCenter(), map.options.minZoom + 1, {
      animate: false,
    });
    map.panInsideBounds(L.latLngBounds(COVERAGE_BOUNDS), { animate: false });
    map.on("resize", onResize);
    return () => void map.off("resize", onResize);
  }, [map]);
  return null;
}

/** Flies to the selected spot once per selection (not on every re-render). */
function FlyToSpot({
  slug,
  location,
  offset,
}: {
  slug: string | undefined;
  location: LatLon | undefined;
  offset: { x: number; y: number };
}) {
  const map = useMap();
  const done = useRef<string | undefined>(undefined);
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  useEffect(() => {
    if (!slug) {
      done.current = undefined;
      return;
    }
    if (!location || done.current === slug) return;
    done.current = slug;
    const zoom = Math.max(map.getZoom(), 14);
    const target = map
      .project([location.lat, location.lon], zoom)
      .add([offsetRef.current.x, offsetRef.current.y]);
    const center = map.unproject(target, zoom);
    if (prefersReducedMotion()) map.setView(center, zoom, { animate: false });
    else map.flyTo(center, zoom, { duration: 0.9 });
  }, [map, slug, location]);

  return null;
}

/** Full-bleed Leaflet map on swisstopo WMTS with clustered spot pins. */
export function OverviewMap({
  spots,
  layer,
  selectedSlug,
  selectedLocation,
  flyOffset,
  onSelect,
  onMapReady,
  pinLabel,
  clusterLabel,
  ariaLabel,
  wind,
  precip,
}: Props) {
  const base = BASE_LAYERS[layer];
  return (
    <div className="map-root">
      <MapContainer
        bounds={SWITZERLAND_BOUNDS}
        maxZoom={MAX_ZOOM}
        minZoom={5}
        zoomControl={false}
        attributionControl={false}
        worldCopyJump={false}
        maxBounds={L.latLngBounds(COVERAGE_BOUNDS)}
        maxBoundsViscosity={1}
        style={{ width: "100%", height: "100%" }}
      >
        <MapBridge onReady={onMapReady} label={ariaLabel} />
        <CoverageGuard />
        <TileLayer
          key={layer}
          url={wmtsUrl(base)}
          maxZoom={MAX_ZOOM}
          maxNativeZoom={base.maxNativeZoom}
        />
        <TileLayer
          url={wmtsUrl(BATHY_OVERLAY)}
          maxZoom={MAX_ZOOM}
          maxNativeZoom={BATHY_OVERLAY.maxNativeZoom}
          minZoom={BATHY_MIN_ZOOM}
          opacity={0.45}
          className="bathy-overlay"
        />
        {precip ? (
          <PrecipLayer field={precip.field} t={precip.t} aerial={layer === "aerial"} />
        ) : null}
        {wind ? <WindLayer field={wind.field} t={wind.t} aerial={layer === "aerial"} /> : null}
        <SpotCluster
          spots={spots}
          selectedSlug={selectedSlug}
          onSelect={onSelect}
          pinLabel={pinLabel}
          clusterLabel={clusterLabel}
        />
        <FlyToSpot slug={selectedSlug} location={selectedLocation} offset={flyOffset} />
      </MapContainer>
    </div>
  );
}
